import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mic2, Brain, Briefcase, ImageIcon } from "lucide-react";
import { Nav, Footer } from "./Landing";

// Hero image URLs are intentionally left empty so users can drop in dedicated
// hero photos per program. Until a real hero image is provided, a styled
// placeholder is rendered.
const PROGRAMS = {
  "blast": {
    title: "B.L.A.S.T. Academy",
    subtitle: "Original B.L.A.S.T. Academy Multimedia Workshop",
    tagline: "Media · Podcasting · Broadcasting · Livestreaming",
    accent: "#0891B2",
    accentLight: "#ECFEFF",
    borderClass: "border-[#00E5FF]/40",
    badgeClass: "bg-[#ECFEFF] text-[#0891B2] border-[#00E5FF]/40",
    chipColor: "#00E5FF",
    Icon: Mic2,
    logo: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/nkjuum7i_JPEG%20image-47AC-B9D5-CA-0.jpeg",
    heroImage: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/3zggo1su_Screenshot%202026-05-19%20at%207.38.01%E2%80%AFPM.png",
    paragraphs: [
      "The B.L.A.S.T. Academy — also known as Broadcast Live Audio and Streaming Technology — is a hands-on multimedia workshop designed to introduce young people to the world of podcasting, audio production, video production, livestreaming, interviewing, and digital storytelling.",
      "Students learn how to use real production tools, develop confidence on camera and behind the scenes, and understand how media can be used to express ideas, document culture, and build opportunities.",
      "The workshop helps participants strengthen their communication skills, creative confidence, teamwork, and technical literacy — all while producing a finished media project they can present or showcase.",
    ],
    highlights: [
      "Broadcast Live Audio and Streaming Technology",
      "hands-on multimedia workshop",
      "podcasting", "audio production", "video production", "livestreaming",
      "digital storytelling", "real production tools",
      "communication skills", "creative confidence", "teamwork", "technical literacy",
      "finished media project",
    ],
    blurb: "",
    skills: [
      "Podcasting", "Audio Production", "Video Production", "Livestreaming",
      "Interviewing", "Editing", "Storytelling", "Final Media Project",
    ],
  },
  "ai-advantage": {
    title: "The Ai Advantage",
    subtitle: "The Ai Advantage Workshop",
    tagline: "Ai for Business · Creativity · Marketing · Modern Entrepreneurship",
    accent: "#9D4CDD",
    accentLight: "#FAF5FE",
    borderClass: "border-[#9D4CDD]/40",
    badgeClass: "bg-[#FAF5FE] text-[#9D4CDD] border-[#9D4CDD]/40",
    chipColor: "#9D4CDD",
    Icon: Brain,
    logo: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/tyv38ur1_1B2510DF-C0BF-41C1-9F32-AD6A95D530B4.PNG",
    heroImage: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/ne5mh8lq_8F9A7297-B21E-4A61-B18B-2027165715F2.PNG",
    paragraphs: [
      "The Ai Advantage teaches young people how to use artificial intelligence as a practical tool for creativity, business, research, marketing, and problem-solving.",
      "Participants learn how to write effective prompts, generate ideas, create content, develop simple brand concepts, explore digital products, and use Ai to support entrepreneurship in today's modern business landscape.",
      "The workshop is designed to make Ai accessible, useful, and empowering — showing students how these tools can help them compete, create, and think strategically. By the end of the program, students gain practical Ai literacy and complete a project or pitch that demonstrates how Ai can be used in real-world business or creative applications.",
    ],
    highlights: [
      "artificial intelligence", "creativity", "business", "research", "marketing", "problem-solving",
      "effective prompts", "generate ideas", "create content", "brand concepts", "digital products",
      "entrepreneurship", "Ai accessible", "compete, create, and think strategically",
      "Ai literacy", "project or pitch", "real-world business or creative applications",
    ],
    blurb: "",
    skills: [
      "Ai Tool Literacy", "Prompt Writing", "Research", "Business Idea Development",
      "Branding Basics", "Marketing Content", "Digital Products", "Final Pitch",
    ],
  },
  "young-hustlers": {
    title: "Young Hustlers Academy",
    subtitle: "Young Hustlers Academy Workshop",
    tagline: "Entrepreneurship · Leadership · Financial Literacy · Career Readiness",
    accent: "#CA8A04",
    accentLight: "#FEFCE8",
    borderClass: "border-[#CA8A04]/40",
    badgeClass: "bg-[#FEFCE8] text-[#CA8A04] border-[#CA8A04]/40",
    chipColor: "#FFD700",
    Icon: Briefcase,
    logo: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/057qpike_IMG_0921.PNG",
    heroImage: "https://customer-assets.emergentagent.com/job_blast-academy-2/artifacts/mkumk8jd_IMG_0322.jpeg",
    comingSoon: true,
    paragraphs: [
      "Young Hustlers Academy is an entrepreneurship and life-skills workshop that gives young people a practical introduction to business, financial literacy, leadership, communication, and real-world opportunity building.",
      "The program focuses on lessons many students do not always receive in traditional school settings — including money mindset, sales, branding, public speaking, teamwork, career pathways, trades, and the basics of launching an idea.",
      "Students are encouraged to think like builders, problem-solvers, and leaders while developing confidence in how they present themselves and their ideas. The workshop helps participants walk away with stronger business awareness, improved communication skills, and a clearer understanding of how to turn creativity and ambition into action.",
    ],
    highlights: [
      "entrepreneurship and life-skills", "business", "financial literacy", "leadership", "communication",
      "real-world opportunity building", "money mindset", "sales", "branding", "public speaking",
      "career pathways", "trades", "launching an idea",
      "builders, problem-solvers, and leaders",
      "business awareness", "communication skills", "creativity and ambition into action",
    ],
    blurb: "",
    skills: [
      "Business Basics", "Money Mindset", "Public Speaking", "Sales & Communication",
      "Career Readiness", "Team Building", "Leadership", "Entrepreneurial Thinking",
    ],
  },
};

const ORDER = ["blast", "ai-advantage", "young-hustlers"];

// Renders a paragraph string with selected phrases wrapped in an accent-colored span.
function renderHighlighted(text, highlights, accent) {
  if (!highlights || highlights.length === 0) return text;
  // Sort longest-first so multi-word phrases match before single words inside them.
  const sorted = [...highlights].sort((a, b) => b.length - a.length);
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`(${sorted.map(escape).join("|")})`, "gi");
  const parts = text.split(pattern);
  return parts.map((part, i) => {
    const key = `${i}-${part.slice(0, 20)}`;
    return sorted.some((h) => h.toLowerCase() === part.toLowerCase()) ? (
      <span key={key} className="font-semibold" style={{ color: accent }}>
        {part}
      </span>
    ) : (
      <span key={key}>{part}</span>
    );
  });
}

export default function ProgramDetail() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const data = PROGRAMS[slug];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!data) {
    return (
      <main className="bg-[#FAF6EC] min-h-screen text-zinc-900">
        <Nav />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center">
          <h1 className="font-display text-4xl font-semibold mb-4">Program not found.</h1>
          <p className="text-zinc-600 mb-8">The program you're looking for doesn't exist.</p>
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#E60000] text-white px-6 py-3.5 text-sm uppercase tracking-[0.18em] font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const { Icon } = data;
  const currentIdx = ORDER.indexOf(slug);
  const nextSlug = ORDER[(currentIdx + 1) % ORDER.length];
  const nextProgram = PROGRAMS[nextSlug];

  return (
    <main data-testid={`program-page-${slug}`} className="bg-[#FAF6EC] min-h-screen text-zinc-900">
      <Nav />

      {/* Hero */}
      <section className="relative pt-44 sm:pt-40 md:pt-36 pb-12 md:pb-16">
        <div
          className="absolute inset-0 -z-10 opacity-50"
          style={{
            background: `radial-gradient(ellipse at top right, ${data.accent}22, transparent 60%), radial-gradient(ellipse at bottom left, ${data.accent}14, transparent 60%)`,
          }}
        />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Link
            to="/"
            data-testid="program-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-zinc-600 hover:text-zinc-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to All Programs
          </Link>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* LEFT — title + meta */}
            <div className="lg:col-span-9">
              <div className="flex items-center gap-3 mb-5">
                <span
                  className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] font-medium border ${data.badgeClass}`}
                  data-testid="program-track-badge"
                >
                  Track {String(currentIdx + 1).padStart(2, "0")}
                </span>
                <span className="h-px w-8" style={{ background: data.accent }} />
                <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-600">{data.tagline}</span>
              </div>
              <h1 data-testid="program-title" className="sr-only">{data.title}</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Hero image placeholder */}
      <section className="pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          {data.heroImage ? (
            <div className="relative w-full flex justify-center overflow-hidden border border-zinc-900/10">
              <img
                src={data.heroImage}
                alt={data.title}
                className={`block w-auto max-h-[640px] object-contain ${data.comingSoon ? "opacity-40" : ""}`}
                data-testid="program-hero-image"
              />
              {data.comingSoon && (
                <div
                  data-testid="program-coming-soon"
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <div className="text-center">
                    <div
                      className="inline-block px-6 py-3 mb-4 text-[10px] uppercase tracking-[0.4em] font-medium border bg-white/80 backdrop-blur-sm"
                      style={{ color: data.accent, borderColor: `${data.accent}66` }}
                    >
                      Launching Soon
                    </div>
                    <div className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight text-zinc-900 drop-shadow-[0_2px_12px_rgba(255,255,255,0.8)]">
                      Coming Soon
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              data-testid="program-hero-placeholder"
              className="relative w-full aspect-[16/9] md:aspect-[21/9] border-2 border-dashed flex items-center justify-center overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${data.accentLight} 0%, #FFFFFF 100%)`,
                borderColor: `${data.accent}55`,
              }}
            >
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />
              <div className="relative text-center px-6">
                <div
                  className="inline-flex items-center justify-center w-14 h-14 mb-4 border"
                  style={{ borderColor: `${data.accent}55`, background: "white" }}
                >
                  <ImageIcon className="w-6 h-6" style={{ color: data.accent }} />
                </div>
                <div className="font-display text-xl md:text-2xl font-semibold text-zinc-900 mb-2">
                  Hero Image
                </div>
                <p className="text-sm text-zinc-600 max-w-md mx-auto">
                  A dedicated hero photo for {data.title} will appear here.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Blurb */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-8" style={{ background: data.accent }} />
            <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-500">About the Workshop</span>
          </div>
          <div
            data-testid="program-blurb"
            className="space-y-5 md:space-y-6 text-zinc-700 text-base md:text-lg lg:text-xl leading-[1.75] font-sans"
          >
            {(data.paragraphs || []).map((para) => (
              <p key={para.slice(0, 32)}>{renderHighlighted(para, data.highlights, data.accent)}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="pb-20 md:pb-28 bg-[#FFFFFF] border-y border-zinc-900/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 md:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-8" style={{ background: data.accent }} />
                <span className="text-[10px] uppercase tracking-[0.32em] text-zinc-500">
                  What Students Learn
                </span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
                Skills &amp; Outcomes
              </h2>
              <p className="mt-4 text-zinc-600 text-sm md:text-base leading-relaxed">
                Practical, hands-on, project-based learning students can put to work right away.
              </p>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {data.skills.map((s) => (
                <div
                  key={s}
                  data-testid={`program-skill-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="flex items-center gap-3 py-3 border-b border-zinc-900/5"
                >
                  <span
                    className="w-2 h-2 rotate-45 shrink-0"
                    style={{ background: data.accent }}
                  />
                  <span className="text-sm md:text-base text-zinc-800">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight text-balance">
            Want to bring <span style={{ color: data.accent }}>{data.title}</span> to your community?
          </h2>
          <p className="mt-6 text-zinc-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Each workshop can be customized for schools, nonprofits, community organizations,
            and grant-funded youth programs across NYC's five boroughs.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href="/#contact"
              data-testid="program-cta-contact"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                setTimeout(() => {
                  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
              className="group inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#E60000] text-white px-7 py-4 text-sm uppercase tracking-[0.18em] font-medium shadow-[0_0_28px_rgba(255,42,42,0.4)] hover:shadow-[0_0_44px_rgba(255,42,42,0.6)] transition-all"
            >
              Request Program Info
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <Link
              to={`/programs/${nextSlug}`}
              data-testid="program-cta-next"
              className="inline-flex items-center gap-2 border border-zinc-900/20 hover:border-zinc-900/50 hover:bg-zinc-900/5 text-zinc-900 px-7 py-4 text-sm uppercase tracking-[0.18em] font-medium transition-all"
            >
              Next: {nextProgram.title}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
